/**
 * Auth actions script file
 *
 * @package    Actions
 * @author     Vadym Karpenko <vkarpenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */

import AuthApi from 'Api/auth';
import { push } from 'react-router-redux';

export const LOGIN_REQUEST = 'LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_ERROR = 'LOGIN_ERROR';

export const LOGIN_SOCIAL_REQUEST = 'LOGIN_SOCIAL_REQUEST';
export const LOGIN_SOCIAL_SUCCESS = 'LOGIN_SOCIAL_SUCCESS';
export const LOGIN_SOCIAL_ERROR = 'LOGIN_SOCIAL_ERROR';

export const LOGOUT_REQUEST = 'LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'LOGOUT_ERROR';

export const KEEP_ALIVE_REQUEST = 'KEEP_ALIVE_REQUEST';
export const KEEP_ALIVE_SUCCESS = 'KEEP_ALIVE_SUCCESS';
export const KEEP_ALIVE_ERROR = 'KEEP_ALIVE_ERROR';

export const CHANGE_ROLE_SUCCESS = 'CHANGE_ROLE_SUCCESS';
export const AUTHORIZE_SUCCESS = 'AUTHORIZE_SUCCESS';
export const UN_AUTHORIZE_SUCCESS = 'UN_AUTHORIZE_SUCCESS';

export const REGISTER_REQUEST = 'REGISTER_REQUEST';
export const REGISTER_SUCCESS = 'REGISTER_SUCCESS';
export const REGISTER_ERROR = 'REGISTER_ERROR';
export const REGISTER_DATA_SUCCESS = 'REGISTER_DATA_SUCCESS';

export const RECOVER_PASSWORD_REQUEST = 'RECOVER_PASSWORD_REQUEST';
export const RECOVER_PASSWORD_SUCCESS = 'RECOVER_PASSWORD_SUCCESS';
export const RECOVER_PASSWORD_ERROR = 'RECOVER_PASSWORD_ERROR';

export const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'RESET_PASSWORD_ERROR';

export const VERIFY_EMAIL_REQUEST = 'VERIFY_EMAIL_REQUEST';
export const VERIFY_EMAIL_SUCCESS = 'VERIFY_EMAIL_SUCCESS';
export const VERIFY_EMAIL_ERROR = 'VERIFY_EMAIL_ERROR';

export const CHECK_RESET_PASSWORD_TOKEN_REQUEST = 'CHECK_RESET_PASSWORD_TOKEN_REQUEST';
export const CHECK_RESET_PASSWORD_TOKEN_SUCCESS = 'CHECK_RESET_PASSWORD_TOKEN_SUCCESS';
export const CHECK_RESET_PASSWORD_TOKEN_ERROR = 'CHECK_RESET_PASSWORD_TOKEN_ERROR';

/**
 * Login user
 *
 * @param {Object} data
 * @param {Object} reviewer
 *
 * @returns {Object}
 */
export function login(data, reviewer) {
    return {
        requestType: 'LOGIN',
        request: AuthApi.login(data),
        success: (response, dispatch) => {
            dispatch(authorize(response.data));
            if (reviewer) {
                dispatch(push(`/submissions/${reviewer.submission_id}`));
            }

            return {
                payload: response.data,
            };
        },
    };
}

/**
 * Login user to system
 *
 * @param {Object} data
 *
 * @returns {*}
 */
export function loginSocial(data) {
    return {
        requestType: 'LOGIN_SOCIAL',
        request: AuthApi.loginSocial(data),
        requestData: data,
        success: (response, dispatch) => {
            if (response.data && response.data.user && response.data.user.id) {
                dispatch(authorize(response.data));
            }

            return {
                payload: response,
            };
        },
    };
}

/**
 * Register user to system
 *
 * @param {Object} data
 *
 * @returns {*}
 */
export function registerSocial(data) {
    return {
        requestType: 'LOGIN_SOCIAL',
        request: AuthApi.registerSocial(data),
        requestData: data,
        success: (response, dispatch) => {
            if (response.data ){
                if(response.data.user && response.data.user.id) {
                    dispatch(authorize(response.data));
                } else if(response.data.orcid_id) {
                    dispatch({
                        type: REGISTER_DATA_SUCCESS,
                        payload: response.data,
                    });
                }
            }

            return {
                payload: response,
            };
        },
    };
}

/**
 * Logout user
 *
 * @returns {Object}
 */
export function logout() {
    return {
        requestType: 'LOGOUT',
        request: AuthApi.logout(),
        success: (response, dispatch) => {
            dispatch(unAuthorize());
            return {
                payload: response.data,
            };
        },
    };
}

/**
 * Authorize user
 *
 * @param {Object} data
 *
 * @returns {Object}
 */
export function authorize(data = {}) {
    if (data && data.token && data.user && data.user.id) {
        return {
            type: AUTHORIZE_SUCCESS,
            payload: data,
        };
    }
}

/**
 * Change user role
 *
 * @returns {Object}
 */
export function changeRole(data = {}) {
    return {
        type: CHANGE_ROLE_SUCCESS,
        payload: data,
    };
}

/**
 * UnAuthorize user
 *
 * @returns {Object}
 */
export function unAuthorize() {
    return {
        type: UN_AUTHORIZE_SUCCESS,
    };
}

/**
 * Keep alive user
 *
 * @returns {Object}
 */
export function keepAlive() {
    return {
        requestType: 'KEEP_ALIVE',
        request: AuthApi.keepAlive(),
        error: (response, dispatch) => {
            if (response.status === 401) {
                dispatch(unAuthorize());
            }

            return {
                payload: response.data,
            };
        },
    };
}

/**
 * Register user
 *
 * @param data
 * @returns {{requestType: string, request: *}}
 */
export function register(data) {
    return {
        requestType: 'REGISTER',
        request: AuthApi.register(data),
        success: (response, dispatch) => {
            dispatch(push('/login'));

            return {
                payload: response.data,
            };
        },
    };
}

/**
 * Register user
 *
 * @param token
 * @param data
 * @returns {{requestType: string, request: *}}
 */
export function registerQuick(token, data) {
    return {
        requestType: 'REGISTER',
        request: AuthApi.registerQuick(token, data),
        success: (response, dispatch) => {
            dispatch(push('/login'));

            return {
                payload: response.data,
            };
        },
    };
}

/**
 * Reset user password request
 *
 * @param data
 * @returns {{requestType: string, request: *}}
 */
export function recoverPassword(data) {
    return {
        requestType: 'RECOVER_PASSWORD',
        request: AuthApi.recoverPassword(data),
        success: (response, dispatch) => {
            dispatch(push('/login'));

            return {
                payload: response.data,
            };
        },
    };
}

/**
 * Reset user password request
 *
 * @param data
 * @returns {{requestType: string, request: *}}
 */
export function resetPassword(data) {
    return {
        requestType: 'RESET_PASSWORD',
        request: AuthApi.resetPassword(data),
        success: (response, dispatch) => {
            dispatch(push('/login'));

            return {
                payload: response.data,
            };
        },
    };
}

/**
 * @param token
 * @returns {{requestType: string, request: *}}
 */
export function checkResetPasswordToken(token) {
    return {
        requestType: 'CHECK_RESET_PASSWORD_TOKEN',
        request: AuthApi.checkResetPasswordToken(token)
    };
}

/**
 * Verify email
 *
 * @param token
 * @returns {{requestType: string, request: *}}
 */
export function emailVerify(token) {
    return {
        requestType: 'VERIFY_EMAIL',
        request: AuthApi.emailVerify(token),
        success: (response, dispatch) => {
            dispatch(push('/login'));
            return {
                payload: response.data,
            };
        },
    };
}



// WEBPACK FOOTER //
// ./src/actions/auth.js